import { MenuItem } from '@vapor/material'
import { Select, SelectProps, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChargeCategoryFragment, useChargeCategoriesQuery } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'

type ChargeCategoriesSingleSelectProps = Omit<SelectProps, 'onChange' | 'value' | 'disabled' | 'error'> & {
  value: string | undefined
  optionsAsInput?: true
  options?: ChargeCategoryFragment[]

  onChange?: (value: string | undefined) => void
  onChangeChargeCategory?: (value: ChargeCategoryFragment | undefined) => void
  filterChargeCategory?: (chargeCategory: ChargeCategoryFragment) => boolean
  error?: string
  disabled?: boolean
  propertyId: string
  addEmptyOption?: true
  stayIncluded?: true
  stayNotIncluded?: true
  cityTaxIncluded?: true
}

export function ChargeCategoriesSingleSelect(props: ChargeCategoriesSingleSelectProps) {
  const { id: tenantId } = useTenant()
  const {
    value,
    onChange,
    optionsAsInput,
    options,
    error,
    disabled,
    propertyId,
    addEmptyOption,
    stayIncluded,
    stayNotIncluded,
    cityTaxIncluded,
    onChangeChargeCategory,
    filterChargeCategory,
    ...otherProps
  } = props

  const [selectedChargeCategoryId, setSelectedChargeCategoryId] = useState('')

  const [chargeCategoriesData, setChargeCategoriesData] = useState(options)
  const { loading: loadingChargeCategory } = useChargeCategoriesQuery({
    variables: {
      filter: {
        tenantId: {
          eq: tenantId,
        },
        propertyId: {
          eq: propertyId,
        },
      },
    },
    skip: optionsAsInput,
    onCompleted: (data) => {
      setChargeCategoriesData(data.chargeCategories ?? [])
      if (value && value !== '' && data?.chargeCategories?.map((cc) => cc.id).includes(value) && selectedChargeCategoryId === '') {
        setSelectedChargeCategoryId(value)
      }
    },
  })

  useEffect(() => {
    if (value !== selectedChargeCategoryId && selectedChargeCategoryId !== '') {
      onChange?.(selectedChargeCategoryId)
    } else if (addEmptyOption) {
      onChange?.(selectedChargeCategoryId === '' ? undefined : selectedChargeCategoryId)
    }
  }, [selectedChargeCategoryId])

  const filterChargeCategories = useCallback(
    (cc: ChargeCategoryFragment) => {
      if (stayIncluded && cc.stayIncluded && !cc.cityTax) return true
      if( stayNotIncluded && cityTaxIncluded && !cc.stayIncluded && cc.cityTax ) return true
      if(stayNotIncluded && !cc.stayIncluded && !cc.cityTax) return true

      return !stayIncluded && !stayNotIncluded && !cityTaxIncluded
    },
    [stayIncluded, stayNotIncluded, cityTaxIncluded],
  )

  const chargeCategories = useMemo(() => {
    return chargeCategoriesData
      ?.filter((x): x is ChargeCategoryFragment => x !== null)
      ?.filter((cc) => filterChargeCategories(cc))
      ?.filter((cc) => !filterChargeCategory || (filterChargeCategory && filterChargeCategory(cc)))
  }, [chargeCategoriesData, stayIncluded, filterChargeCategory])

  useEffect(() => {
    if (chargeCategories?.map((cc) => cc.id).includes(value ?? '')) {
      setSelectedChargeCategoryId(value ?? '')
    } else {
      setSelectedChargeCategoryId('')
    }
  }, [value, chargeCategories])

  useEffect(() => {
    if (optionsAsInput && options) {
      if (value && value !== '' && options.map((cc) => cc.id).includes(value) && selectedChargeCategoryId === '') {
        setSelectedChargeCategoryId(value)
      }
    }
  }, [options, optionsAsInput, value, selectedChargeCategoryId])

  return (
    <Select
      {...otherProps}
      value={(() => (chargeCategories?.map((cc) => cc.id).includes(selectedChargeCategoryId ?? '') ? selectedChargeCategoryId : ''))()}
      onChange={(event) => {
        setSelectedChargeCategoryId(event.target.value as string)
        if (onChangeChargeCategory) onChangeChargeCategory(chargeCategories?.find((cc) => cc.id === (event.target.value as string)))
      }}
      error={error !== undefined}
      disabled={disabled || loadingChargeCategory}
      sx={{
        ...otherProps.sx,
        '& .MuiSelect-select': {
          width: 'calc(100% - 35px)',
        },
      }}
    >
      {addEmptyOption && <MenuItem key={''} value={''} style={{ height: 44 }}></MenuItem>}
      {chargeCategories?.map((chargeCategory) => (
        <MenuItem key={chargeCategory.id} value={chargeCategory.id}>
          <Typography variant="body">{chargeCategory?.name}</Typography>
        </MenuItem>
      ))}
    </Select>
  )
}
